import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useRole } from "../context/RoleContext";
import { getReview, downloadReviewDecisionPdf } from "../lib/cityPortal";
import { assetUrl, formatInt, formatPriceFull, formatTypology } from "../lib/format";
import { ChevronRight, Download, ExternalLink, Info, Check, X } from "lucide-react";
import { relativeDate } from "../lib/ownerUi";
import BackBar from "../components/BackBar";

const CHECKLIST_ITEMS = [
  { key: "zoning", label: "Zoning compatibility verified." },
  { key: "permits", label: "Required permits documented." },
  { key: "constraints", label: "Constraint flags addressed (historic, easement, etc.)." },
  { key: "quality", label: "Architectural quality threshold met." },
];

const STATE_TONE = {
  "In Review": { fg: "#8a651f", bg: "rgba(185,138,60,0.14)" },
  Approved: { fg: "#4F6149", bg: "rgba(79,97,73,0.10)" },
  Rejected: { fg: "#A23B2E", bg: "rgba(162,59,46,0.10)" },
  "Needs Revision": { fg: "#1B1F4B", bg: "rgba(91,97,150,0.12)" },
  Draft: { fg: "#6C6B85", bg: "rgba(154,160,192,0.20)" },
};

export default function CityReviewDetail() {
  const { designId } = useParams();
  const { user } = useRole();
  const navigate = useNavigate();
  const [d, setD] = useState(null);
  const [err, setErr] = useState(null);
  const [downloading, setDownloading] = useState(false);

  const handleDownloadPacket = async () => {
    if (!user?.id || !d) return;
    setDownloading(true);
    try {
      const slug = (d.name || designId)
        .toLowerCase()
        .replace(/[^a-z0-9-_]+/g, "-")
        .replace(/^-+|-+$/g, "")
        .slice(0, 60);
      await downloadReviewDecisionPdf(designId, user.id,
        `design-review-packet-${slug}.pdf`);
    } catch (_) {
      // Surface inline; for now silent — button shows a re-try state.
    } finally {
      setDownloading(false);
    }
  };

  useEffect(() => {
    if (!user?.id) return;
    let alive = true;
    getReview(designId, user.id)
      .then((next) => alive && setD(next))
      .catch((e) => alive && setErr(e));
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id, designId]);

  if (err) {
    return <p style={{ color: "#A23B2E" }} data-testid="city-review-error">Could not load this design.</p>;
  }
  if (!d) {
    return <div style={{ color: "#6C6B85", fontStyle: "italic" }} data-testid="city-review-loading">Loading design…</div>;
  }

  const tone = STATE_TONE[d.status] || STATE_TONE.Draft;
  const compliance = d.compliance_preview || {};
  // Architect's self-attestation is what we render. There is no "edit" path —
  // permitting decisions are governed by Accela, this view is observational.
  const attestation = {
    zoning: !!compliance.zoning_ok,
    permits: !!compliance.permits_ok,
    constraints: !compliance.constraints_flagged,
    quality: true,
  };

  // Accela mock deep link. The Accela case (if any) is exposed via the
  // accela_ref on the linked project — for now we link to a search by parcel
  // address so it's stable even when no case exists yet.
  const accelaSearch = encodeURIComponent(d.parcel?.address || d.name || "");
  const accelaUrl =
    `https://www.indy.gov/activity/apply-for-a-permit?q=${accelaSearch}`;

  return (
    <section className="animate-fade-in-up" data-testid="city-review-detail">
      <BackBar
        parent={{ to: "/city/reviews", label: "Design Review" }}
        trail={[
          { label: "City", to: "/city" },
          { label: "Design Review", to: "/city/reviews" },
          { label: d.name },
        ]}
      />

      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1
            style={{
              fontFamily: '"Fraunces", Georgia, serif',
              fontSize: "clamp(26px, 3.6vw, 32px)",
              fontWeight: 600,
              color: "#1B1F4B",
            }}
            data-testid="city-review-title"
          >
            {d.name}
          </h1>
          <div
            style={{
              fontFamily: '"Newsreader", Georgia, serif',
              fontStyle: "italic",
              color: "#6C6B85",
              fontSize: 14,
              marginTop: 2,
            }}
          >
            {d.architect ? (
              <>
                <Link to={`/architects/${d.architect.id}`} style={{ color: "#1B1F4B", fontWeight: 600 }}>
                  {d.architect.firm}
                </Link>
                {" · "}submitted {relativeDate(d.created_at || d.updated_at)}
              </>
            ) : (
              <>submitted {relativeDate(d.created_at || d.updated_at)}</>
            )}
          </div>
        </div>
        <span
          style={{
            padding: "3px 12px",
            borderRadius: 999,
            background: tone.bg,
            color: tone.fg,
            fontSize: 11,
            fontWeight: 700,
            letterSpacing: "0.05em",
            textTransform: "uppercase",
            whiteSpace: "nowrap",
          }}
          data-testid="city-review-status"
        >
          {d.status}
        </span>
      </div>

      <div className="mt-7 grid lg:grid-cols-[2fr_1fr] gap-7">
        {/* Left — content */}
        <div className="space-y-6">
          <div className="wn-card" style={{ padding: 16 }}>
            <div
              style={{
                width: "100%",
                height: 280,
                backgroundImage: `url(${assetUrl(d.exterior_photo)})`,
                backgroundSize: "cover",
                backgroundPosition: "center",
                borderRadius: 4,
              }}
              aria-hidden
            />
            {d.floor_plan_svg && (
              <div
                className="mt-3"
                style={{
                  width: "100%",
                  height: 180,
                  background: "#FBFAFD",
                  backgroundImage: `url(${assetUrl(d.floor_plan_svg)})`,
                  backgroundSize: "contain",
                  backgroundPosition: "center",
                  backgroundRepeat: "no-repeat",
                  borderRadius: 4,
                  border: "1px solid #D9D8E4",
                }}
                aria-hidden
              />
            )}
          </div>

          <div className="wn-card" style={{ padding: 18 }}>
            <h2
              style={{
                fontFamily: '"Fraunces", Georgia, serif',
                fontWeight: 600,
                fontSize: 18,
                color: "#1B1F4B",
              }}
            >
              Specifications.
            </h2>
            <dl
              className="mt-3 grid grid-cols-2 gap-y-2 gap-x-6 tabular-nums"
              style={{ fontSize: 13, color: "#272636" }}
            >
              <Spec k="Typology" v={formatTypology(d.typology)} />
              <Spec k="Style" v={(d.style || "—").replace(/_/g, " ")} />
              <Spec k="Sq Ft" v={formatInt(d.sqft)} />
              <Spec k="Beds / Baths" v={`${d.beds} / ${d.baths}`} />
              <Spec k="Min lot width" v={`${d.min_lot_width || "—"}′`} />
              <Spec k="Zoning fits" v={(d.zoning_fits || []).join(", ") || "—"} />
              <Spec k="Adjusted 2026" v={formatPriceFull(d.adjusted_cost_2026)} />
              <Spec k="Plan price" v={formatPriceFull(d.plan_purchase_price)} />
            </dl>
            {(d.lifestyle_tags || []).length > 0 && (
              <div className="mt-3 flex flex-wrap gap-1.5">
                {(d.lifestyle_tags || []).map((t) => (
                  <span
                    key={t}
                    style={{
                      padding: "2px 8px",
                      borderRadius: 999,
                      border: "1px solid #D9D8E4",
                      background: "#FBFAFD",
                      color: "#1B1F4B",
                      fontSize: 11,
                      fontWeight: 600,
                    }}
                  >
                    {t.replace(/_/g, " ")}
                  </span>
                ))}
              </div>
            )}
          </div>

          {(d.review_history || []).length > 0 && (
            <div className="wn-card" style={{ padding: 18 }} data-testid="city-review-history">
              <h2
                style={{
                  fontFamily: '"Fraunces", Georgia, serif',
                  fontWeight: 600,
                  fontSize: 18,
                  color: "#1B1F4B",
                }}
              >
                Submission history.
              </h2>
              <div className="mt-3 space-y-3">
                {(d.review_history || []).map((h, i) => (
                  <div key={i} className="flex items-start gap-3">
                    <span
                      aria-hidden
                      style={{
                        marginTop: 6,
                        width: 8,
                        height: 8,
                        borderRadius: 999,
                        background: "#B14E2F",
                        flexShrink: 0,
                      }}
                    />
                    <div>
                      <div style={{ fontSize: 13, color: "#272636" }}>
                        <span style={{ fontWeight: 700 }}>{h.state}</span>
                        {h.actor_id && <> · {h.actor_id}</>}
                      </div>
                      <div
                        style={{
                          fontFamily: '"Newsreader", Georgia, serif',
                          fontStyle: "italic",
                          color: "#6C6B85",
                          fontSize: 11.5,
                        }}
                      >
                        {relativeDate(h.at)}
                      </div>
                      {h.note && (
                        <div style={{ color: "#272636", fontSize: 12.5, marginTop: 3 }}>
                          {h.note}
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Right — observational status panel (read-only, decisions live in Accela) */}
        <aside data-testid="city-review-panel">
          <div className="wn-card" style={{ padding: 18 }}>
            <h2
              style={{
                fontFamily: '"Fraunces", Georgia, serif',
                fontWeight: 600,
                fontSize: 18,
                color: "#1B1F4B",
              }}
            >
              Observed status.
            </h2>

            {/* Governance disclosure — anchors the read-only framing. */}
            <div
              className="mt-3 flex items-start gap-2"
              style={{
                background: "rgba(91,97,150,0.06)",
                border: "1px solid #D9D8E4",
                borderRadius: 4,
                padding: "10px 12px",
              }}
              data-testid="city-review-governance-note"
            >
              <Info size={14} style={{ color: "#5B6196", marginTop: 2, flexShrink: 0 }} />
              <p style={{ fontSize: 12.5, color: "#272636", lineHeight: 1.5, margin: 0 }}>
                Permitting decisions are governed by <strong>Accela</strong>.
                This view reflects the architect's submission and any status
                already recorded.
              </p>
            </div>

            {/* Architect's compliance self-attestation — read-only. */}
            <div className="mt-4">
              <div className="wn-label" style={{ marginBottom: 6 }}>
                Architect self-attestation
              </div>
              <ul className="space-y-2" data-testid="city-review-attestation">
                {CHECKLIST_ITEMS.map((c) => {
                  const ok = attestation[c.key];
                  return (
                    <li
                      key={c.key}
                      className="flex items-start gap-2"
                      style={{ fontSize: 12.5, color: "#272636" }}
                      data-testid={`attestation-${c.key}`}
                    >
                      <span
                        aria-hidden
                        style={{
                          marginTop: 3,
                          width: 14,
                          height: 14,
                          borderRadius: 3,
                          flexShrink: 0,
                          background: ok ? "#4F6149" : "transparent",
                          border: `1px solid ${ok ? "#4F6149" : "#A23B2E"}`,
                          display: "inline-flex",
                          alignItems: "center",
                          justifyContent: "center",
                          color: "#FBFAFD",
                        }}
                      >
                        {ok ? <Check size={10} strokeWidth={3} /> : <X size={10} strokeWidth={3} color="#A23B2E" />}
                      </span>
                      <span>{c.label}</span>
                    </li>
                  );
                })}
              </ul>
            </div>

            {/* Recorded decision — only shown when one exists in our DB. */}
            {d.city_review?.decided_at && (
              <div className="mt-5" data-testid="city-review-recorded">
                <div className="wn-label">Decision of record</div>
                <div
                  style={{
                    fontFamily: '"Fraunces", Georgia, serif',
                    fontWeight: 600,
                    fontSize: 15,
                    color: "#1B1F4B",
                    marginTop: 2,
                  }}
                >
                  {d.city_review.decided_by_name || d.city_review.decided_by || "—"}
                </div>
                <div
                  style={{
                    fontFamily: '"Newsreader", Georgia, serif',
                    fontStyle: "italic",
                    color: "#6C6B85",
                    fontSize: 12,
                  }}
                >
                  {relativeDate(d.city_review.decided_at)}
                </div>
                {d.city_review.note && (
                  <div style={{ color: "#272636", fontSize: 13, marginTop: 8, whiteSpace: "pre-wrap" }}>
                    {d.city_review.note}
                  </div>
                )}
              </div>
            )}

            {/* Actions: download artefact + open in Accela. No decisions. */}
            <div className="mt-5 space-y-2">
              <button
                type="button"
                onClick={handleDownloadPacket}
                disabled={downloading}
                className="wn-btn wn-btn-primary w-full inline-flex items-center"
                style={{ gap: 8, justifyContent: "center" }}
                data-testid="city-review-download-packet"
                title="Download an observational record of this design"
              >
                <Download size={14} />
                {downloading ? "Preparing PDF…" : "Download review packet (PDF)"}
              </button>
              <a
                href={accelaUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="wn-btn wn-btn-outline w-full inline-flex items-center"
                style={{ gap: 8, justifyContent: "center" }}
                data-testid="city-review-open-accela"
              >
                <ExternalLink size={14} /> Open in Accela
              </a>
              {d.status === "Approved" && (
                <button
                  className="wn-btn wn-btn-tertiary mt-1"
                  style={{ paddingLeft: 0 }}
                  onClick={() => navigate(`/marketplace/${d.id}`)}
                  data-testid="city-review-view-marketplace"
                >
                  View in Marketplace <ChevronRight size={14} />
                </button>
              )}
            </div>
          </div>
        </aside>
      </div>
    </section>
  );
}

function Spec({ k, v }) {
  return (
    <>
      <dt style={{ color: "#6C6B85" }}>{k}</dt>
      <dd style={{ fontWeight: 600 }}>{v}</dd>
    </>
  );
}
